import { FC, ReactNode, useCallback, useContext, useMemo } from 'react'
import cn from 'classnames'
import styles from 'components/features/pfm/SubscriptionPane/SubscriptionPanePreset.styl'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import { SubscriptionMessageOverlay } from 'components/features/pfm/SubscriptionMessageOverlay/SubscriptionMessageOverlay'
import { killFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { FloatingPaneHeader } from 'components/features/pfm/FloatingPaneHeader/FloatingPaneHeader'
import { FloatingPaneFooter } from 'components/features/pfm/FloatingPaneFooter/FloatingPaneFooter'
import SvgIcon from 'components/atoms/SvgIcon'
import NewSvg from 'icons/new.svg'
import { PaneContext } from 'components/features/pfm/FloatingPaneHost/FloatingPaneHost'
import { SubscriptionFloatingPane } from 'interfaces/floatingPanes'

type SubscriptionPanePresetProps = {
  title: ReactNode
  subTitle?: ReactNode
  isLoading?: boolean
  cardLayout?: boolean
  primaryButton: ReactNode
  cancelButton?: ReactNode
}

export const SubscriptionPanePreset: FC<SubscriptionPanePresetProps> = (props) => {
  const { cardLayout, children, primaryButton, cancelButton, title, subTitle, isLoading } = props

  const pane = useContext(PaneContext) as SubscriptionFloatingPane

  const dispatch = useStoreDispatch()
  const handleClose = useCallback(() => {
    dispatch(killFloatingPanesActionCreator('subscription'))
  }, [dispatch])

  const badgeChild = useMemo(() => (
    <div className={ cn(styles.star, { [styles.green]: pane.mode === 'done' }) }>
      <SvgIcon src={ NewSvg } />
    </div>
  ), [pane.mode])

  const headerChild = useMemo(() => (
    <FloatingPaneHeader
      title={ title }
      subTitle={ subTitle }
      progressBadge={ badgeChild }
      isLoading={ isLoading }
      theme='golden'
      centered
    />
  ), [title, subTitle, badgeChild, isLoading])

  const footerChild = useMemo(() => (
    <FloatingPaneFooter
      primaryButton={ primaryButton }
      tertiaryButton={ cancelButton }
    />
  ), [cancelButton, primaryButton])

  return (
    <FloatingPaneLayout
      type='subscription'
      notification={ <SubscriptionMessageOverlay /> }
      onClose={ handleClose }
      header={ headerChild }
      footer={ footerChild }
      cardLayout={ cardLayout }
      withStaticHeader
      withStaticFooter
    >
      { children }
    </FloatingPaneLayout>
  )
}
